import {
  type MemberSignalsResponse,
  type Paginated,
  type ReviewMatchRequest,
  type SignalMatchDetail,
  type SignalMatchesListQuerySchema,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contract's own input shape (U3) — never a hand-written copy of the filters. */
type MatchesParams = z.input<typeof SignalMatchesListQuerySchema>;

/**
 * The Trust review queue and its two verdicts.
 *
 * ## Why the verdicts return nothing, and what refreshes instead
 *
 * Both endpoints answer 204. The row's next truth comes from the queue, which
 * this invalidates — rendering a decided match in place from a mutation response
 * would give the screen two sources for one row, and they disagree the moment a
 * colleague rules on it a second later.
 *
 * `confirm` also publishes `members.status.changed` (W5), because it **bans an
 * account**: the staff list, the member detail and anything else showing that
 * account are looking at a lifecycle state this just changed, and they must not
 * need a reload to notice.
 *
 * The `MemberSignals` tag is separate from the queue's: a verdict does not
 * capture or remove a signal, so a dismissal has no business re-fetching the
 * signals panel of whatever member happens to be open.
 */
export const trustApi = createApi({
  reducerPath: 'trustApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['SignalMatches', 'MemberSignals'],
  endpoints: (builder) => ({
    listSignalMatches: builder.query<Paginated<SignalMatchDetail>, MatchesParams>({
      query: (params) => ({ url: '/admin/trust/matches', method: 'GET', params }),
      providesTags: ['SignalMatches'],
    }),

    memberSignals: builder.query<MemberSignalsResponse, string>({
      query: (userId) => ({ url: `/admin/trust/members/${userId}/signals`, method: 'GET' }),
      providesTags: (_result, _error, userId) => [{ type: 'MemberSignals' as const, id: userId }],
    }),

    /** "The same person." Bans the subject account — terminal, and audited. */
    confirmMatch: builder.mutation<
      void,
      { id: string; subjectUserId: string | null } & ReviewMatchRequest
    >({
      query: ({ id, note }) => ({
        url: `/admin/trust/matches/${id}/confirm`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: ['SignalMatches'],
      async onQueryStarted({ subjectUserId }, { queryFulfilled }) {
        try {
          await queryFulfilled;

          if (subjectUserId !== null) {
            EVENT_BUS.publish({ type: 'members.status.changed', userId: subjectUserId });
          }
        } catch {
          // Refused — nothing happened, so there is nothing to announce.
          // Swallowed deliberately: `onQueryStarted` has no caller, so a
          // rejection escaping it becomes an unhandled rejection. The failure
          // is already the caller's, through `unwrap()`.
        }
      },
    }),

    /** "Different people." Clears the row and whitelists the pair, so it stays cleared. */
    dismissMatch: builder.mutation<void, { id: string } & ReviewMatchRequest>({
      query: ({ id, note }) => ({
        url: `/admin/trust/matches/${id}/dismiss`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: ['SignalMatches'],
    }),
  }),
});

export const {
  useListSignalMatchesQuery,
  useMemberSignalsQuery,
  useConfirmMatchMutation,
  useDismissMatchMutation,
} = trustApi;
